// Relative imports (not `@/`) so bun can test this module.
import { axialHexDistance } from '../hexmap/coords';
import { getHexTileMapKey } from '../hexmap/model/keys';
import type { RuleSet } from '../rules';
import {
  BUILD_TASK,
  type Building,
  buildingLevel,
  type Cell,
  type Command,
  type EntityId,
  type SimContext,
  type SimState,
  type TaskLine,
  tileResources,
  type Unit,
  type WorkerPlace,
  workerSlots,
} from '../sim';
import { resourceIcon } from './resources';

/**
 * The City panel's Workers tab as plain data: per task the target, who is on
 * it and, per building of the city, one pip per slot with the workers
 * holding them; the leftovers ("Free"), the pinned workers and the visitors.
 * Built from the city's own workers and buildings only (the sim's `workBoard`
 * counts them), so nothing in fog is described.
 */

export type PipKind = 'pinned' | 'auto' | 'visiting' | 'free';

export interface WorkerChip {
  unitId: EntityId;
  /** "Wk 1". */
  label: string;
  kind: Exclude<PipKind, 'free'>;
  /** A visitor's home city, "C2". */
  home?: string;
}

/** Why a building can't take another worker now, or the idle worker "+ add idle worker" sends. */
export type AddIdle = { unitId: EntityId } | { reason: string };

export interface PlaceRow {
  /** The building's id, or `wild:<task>` for workers on wild tiles. */
  key: string;
  /** What `assign` takes as its target; none for wild tiles. */
  buildingId?: EntityId;
  /** "Farm 1 · L1", "Barracks site", "Wild food". */
  name: string;
  /** A site's build progress in percent. */
  percent?: number;
  /** Slots used and total; none for wild tiles. */
  slots?: { used: number; total: number };
  pips: PipKind[];
  chips: WorkerChip[];
  /** Absent on wild tiles. */
  addIdle?: AddIdle;
}

export interface TaskRow {
  task: string;
  icon: string;
  name: string;
  target: number;
  /** Own workers on it, pinned and auto. */
  now: number;
  visiting: number;
  places: PlaceRow[];
}

export interface HeldRow {
  unitId: EntityId;
  label: string;
  /** "Farm 1 · gathering food". */
  text: string;
}

export interface VisitorRow {
  unitId: EntityId;
  label: string;
  /** Their home city, "City 2". */
  from: string;
  /** Where they work here. */
  where: string;
}

export interface CityWorkers {
  /** The city's own workers (home here) and its worker cap. */
  own: number;
  cap: number;
  tasks: TaskRow[];
  /**
   * Own auto workers holding no task, and what each is doing ("Wk 7 hauling
   * food"); `idleIds` are the city's idle workers as the roster counts them
   * (`Roster.idle`): what "Select idle" cycles.
   */
  free: { unitIds: EntityId[]; idleIds: EntityId[]; text: string };
  /** Own workers working in another city's territory ("Wk 7 → City 2 Farm"): not free. */
  away: Array<{ unitId: EntityId; label: string; text: string }>;
  /** A building sits empty while a worker idles: the fix, as a sentence and as a new target. */
  warn: { text: string; task: string; target: number } | null;
  held: HeldRow[];
  visitors: VisitorRow[];
}

/** The numeric part of an id (`u12` → 12), for ordering and the label "Wk 12". */
function idNumber(id: string): number {
  return Number.parseInt(id.replace(/\D/g, ''), 10) || 0;
}

function byId(a: string, b: string): number {
  return idNumber(a) - idNumber(b) || a.localeCompare(b);
}

/** "Wk 12": the unit type's roster tag and its id number. */
export function workerLabel(rules: RuleSet, unit: Unit): string {
  const tag = rules.units[unit.type]?.ui?.glyph ?? unit.type.slice(0, 2);
  return `${tag} ${idNumber(unit.id)}`;
}

type PinnedOrder = Extract<NonNullable<Unit['order']>, { kind: 'gather' | 'build' }>;

/** The pinned gather or build order `unit` holds (`assign`), if any. */
function pinnedOrder(unit: Unit): PinnedOrder | null {
  const o = unit.order;
  return (o?.kind === 'gather' || o?.kind === 'build') && o.pin === true ? o : null;
}

/** The sum of a targets map. */
export function targetsTotal(targets: Readonly<Record<string, number>> | undefined): number {
  let sum = 0;
  for (const n of Object.values(targets ?? {})) sum += n;
  return sum;
}

/**
 * `targets` with `task` set to `value`: the full map for `setWorkTargets`
 * (zeros dropped), kept within `cap` (the sim clamps in key order, so an
 * over-cap map would cost later tasks). Null when `value` is negative or
 * would take the sum past `cap`.
 */
export function withTarget(
  targets: Readonly<Record<string, number>> | undefined,
  task: string,
  value: number,
  cap: number
): Record<string, number> | null {
  const old = targets?.[task] ?? 0;
  if (value < 0 || (value > old && targetsTotal(targets) - old + value > cap)) return null;
  const out = { ...targets, [task]: value };
  if (value === 0) delete out[task];
  return out;
}

/** `withTarget` one step up or down. */
export function steppedTargets(
  targets: Readonly<Record<string, number>> | undefined,
  task: string,
  delta: 1 | -1,
  cap: number
): Record<string, number> | null {
  return withTarget(targets, task, (targets?.[task] ?? 0) + delta, cap);
}

/** A `setWorkTargets` the panel sent, for `currentTargets`. */
export interface SentTargets {
  cityId: EntityId;
  targets: Record<string, number>;
  /** The city's own targets when it was sent: any change from them means the sim answered. */
  base: Record<string, number>;
  /** The sim tick it was sent on. */
  tick: number;
}

/** Ticks a sent map stands in for the city's own before it is dropped (the sim clamped or refused it). */
export const SENT_TICKS = 100;

function sameTargets(a: Readonly<Record<string, number>>, b: Readonly<Record<string, number>>) {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k]);
}

/**
 * The targets a click builds on: `sent` while the sim hasn't shown it yet
 * (so clicks faster than a tick stack), else the city's `actual` ones. A
 * sent map is dropped once `actual` equals it or differs from the city's
 * targets at send time (the sim applied or trimmed it), for another city,
 * or `SENT_TICKS` after it was sent.
 */
export function currentTargets(
  sent: SentTargets | null,
  cityId: EntityId,
  actual: Readonly<Record<string, number>> | undefined,
  tick: number
): Record<string, number> | undefined {
  if (
    !sent ||
    sent.cityId !== cityId ||
    tick - sent.tick > SENT_TICKS ||
    tick < sent.tick ||
    sameTargets(sent.targets, actual ?? {}) ||
    !sameTargets(sent.base, actual ?? {})
  ) {
    return actual;
  }
  return sent.targets;
}

/** The command "+ add idle worker" sends: pin `unitId` to the building (or site). */
export function assignIdleCommand(unitId: EntityId, buildingId: EntityId): Command {
  return { type: 'assign', unitIds: [unitId], target: buildingId, pin: true };
}

/** The command ✕ and 🔓 send: hand workers back to auto. */
export function releaseCommand(unitIds: readonly EntityId[]): Command {
  return { type: 'automate', unitIds: [...unitIds] };
}

/** The command "Adopt" sends: make `cityId` the home of a visiting worker. */
export function adoptCommand(unitId: EntityId, cityId: EntityId): Command {
  return { type: 'setHome', unitIds: [unitId], cityId };
}

/** The command the steppers send. */
export function setTargetsCommand(cityId: EntityId, targets: Record<string, number>): Command {
  return { type: 'setWorkTargets', cityId, targets };
}

/**
 * The idle worker `+ add idle worker` sends to `at`: of `candidates` the
 * one nearest by hex distance, the lower id on a tie. Null when none.
 */
export function nearestIdle(candidates: readonly Unit[], at: Cell): Unit | null {
  let best: Unit | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const u of candidates) {
    const d = axialHexDistance(u.pos, at);
    if (d < bestDistance || (d === bestDistance && best && byId(u.id, best.id) < 0)) {
      best = u;
      bestDistance = d;
    }
  }
  return best;
}

/** What an unplaced auto worker is up to. */
function idleDoing(rules: RuleSet, unit: Unit): string {
  if (unit.auto?.phase === 'haul' || unit.carry) {
    const res = unit.carry?.res;
    return res ? `hauling ${rules.resources[res]?.name.toLowerCase() ?? res}` : 'hauling';
  }
  return (unit.path?.length ?? 0) > 0 ? 'on the way' : 'no work found';
}

/** Footprint-aware tile keys of a building. */
function keysOf(b: Building): string[] {
  return [b.pos, ...(b.footprint ?? [])].map(getHexTileMapKey);
}

export interface CityWorkersInput {
  state: SimState;
  ctx: SimContext;
  city: Building;
  /** Tile → city id (`territoryOwners`). */
  owners: ReadonlyMap<string, EntityId>;
  /** The sim's `workBoard` for the city (memoised per tick). */
  lines: readonly TaskLine[];
  places: readonly WorkerPlace[];
  /** The city's own workers working elsewhere (`workBoard().away`). */
  away: readonly WorkerPlace[];
  /** The roster's idle worker ids (`tickRoster().idle`, the whole player's). */
  idleIds: readonly EntityId[];
  /** The resources to show a row for (`focusResources`), in display order. */
  resources: readonly string[];
  /** Stable city numbers (`numberCities`). */
  cityNumbers: ReadonlyMap<string, number>;
  /** The city's worker cap (`cityWorkerCap`). */
  cap: number;
}

export function cityWorkers(input: CityWorkersInput): CityWorkers {
  const { state, ctx, city, owners, places, resources, cityNumbers, cap } = input;
  const awayIds = new Set(input.away.map((p) => p.unitId));
  const rosterIdle = new Set(input.idleIds);
  const { rules } = ctx;
  const lineOf = new Map(input.lines.map((l) => [l.task, l]));
  const placeOf = new Map(places.map((p) => [p.unitId, p]));
  const cityNo = (id: string | undefined) => `City ${(id && cityNumbers.get(id)) || '?'}`;

  // The city's own workers: gatherers that call it home.
  const mine = Object.values(state.units)
    .filter(
      (u) =>
        u.owner === city.owner &&
        u.homeCityId === city.id &&
        rules.units[u.type]?.actions.includes('gather')
    )
    .sort((a, b) => byId(a.id, b.id));
  const unitOf = (id: string) => state.units[id];

  // The city's work places, numbered per type ("Farm 1", "Farm 2").
  const here = Object.values(state.buildings)
    .filter((b) => b.owner === city.owner && owners.get(getHexTileMapKey(b.pos)) === city.id)
    .sort((a, b) => byId(a.id, b.id));
  const counted = new Map<string, number>();
  const placeByKey = new Map<string, PlaceRow>();
  const taskOfPlace = new Map<string, string>();
  const buildingOfRow = new Map<string, Building>();
  const rowOfTile = new Map<string, PlaceRow>();
  for (const b of here) {
    const def = rules.buildings[b.type];
    if (!def) continue;
    const site = b.buildProgress < 1;
    if (site ? def.consumesBuilder || b.id === city.id : !def.provides) continue;
    let name = `${def.name} site`;
    let total = 1;
    if (!site) {
      const n = (counted.get(b.type) ?? 0) + 1;
      counted.set(b.type, n);
      name = `${def.name} ${n} · L${buildingLevel(b)}`;
      total = workerSlots(rules, b);
    }
    const row: PlaceRow = {
      key: b.id,
      buildingId: b.id,
      name,
      percent: site ? Math.floor(b.buildProgress * 100) : undefined,
      slots: { used: 0, total },
      pips: [],
      chips: [],
    };
    placeByKey.set(b.id, row);
    buildingOfRow.set(b.id, b);
    taskOfPlace.set(b.id, site ? BUILD_TASK : (def.provides?.resource ?? ''));
    for (const k of keysOf(b)) rowOfTile.set(k, row);
  }

  // Wild tiles' workers, per task.
  const wild = new Map<string, PlaceRow>();
  for (const p of places) {
    const u = unitOf(p.unitId);
    if (!u) continue;
    const chip: WorkerChip = {
      unitId: u.id,
      label: workerLabel(rules, u),
      kind: p.kind,
      home: p.kind === 'visiting' ? `C${cityNumbers.get(u.homeCityId ?? '') ?? '?'}` : undefined,
    };
    const row = p.key ? rowOfTile.get(p.key) : undefined;
    if (row) {
      row.chips.push(chip);
      continue;
    }
    let w = wild.get(p.task);
    if (!w) {
      const name = rules.resources[p.task]?.name.toLowerCase() ?? p.task;
      w = { key: `wild:${p.task}`, name: `Wild ${name}`, pips: [], chips: [] };
      wild.set(p.task, w);
    }
    w.chips.push(chip);
  }

  // Idle candidates: own auto workers holding nothing, not heading anywhere pinned.
  const free = mine.filter(
    (u) => u.auto && !placeOf.has(u.id) && !awayIds.has(u.id) && !pinnedOrder(u)
  );
  // Only the roster's idle workers, and none with a load (hauling), take "+ add idle worker".
  const idleIds = mine.filter((u) => rosterIdle.has(u.id)).map((u) => u.id);
  const candidates = free.filter(
    (u) => rosterIdle.has(u.id) && !u.carry && u.auto?.phase !== 'haul'
  );

  for (const row of placeByKey.values()) {
    row.chips.sort((a, b) => byId(a.unitId, b.unitId));
    const used = row.chips.length;
    const total = row.slots?.total ?? 1;
    if (row.slots) row.slots.used = used;
    const order: PipKind[] = ['pinned', 'auto', 'visiting'];
    row.pips = order.flatMap((k) => row.chips.filter((c) => c.kind === k).map(() => k));
    while (row.pips.length < total) row.pips.push('free');
    const b = buildingOfRow.get(row.key);
    if (!b) continue;
    if (used >= total) row.addIdle = { reason: `${row.name.split(' · ')[0]} is full` };
    else {
      const pick = nearestIdle(candidates, b.pos);
      row.addIdle = pick ? { unitId: pick.id } : { reason: 'No idle worker in this city' };
    }
  }

  const taskIds = [...resources];
  for (const t of lineOf.keys()) if (t !== BUILD_TASK && !taskIds.includes(t)) taskIds.push(t);
  taskIds.push(BUILD_TASK);
  const tasks: TaskRow[] = taskIds.map((task) => {
    const line = lineOf.get(task);
    const rows = [...placeByKey.values()].filter((r) => taskOfPlace.get(r.key) === task);
    const w = wild.get(task);
    return {
      task,
      icon: task === BUILD_TASK ? '🔨' : resourceIcon(task),
      name: task === BUILD_TASK ? 'Build' : (rules.resources[task]?.name ?? task),
      target: line?.target ?? 0,
      now: (line?.pinned ?? 0) + (line?.auto ?? 0),
      visiting: line?.visiting ?? 0,
      places: w ? [...rows, w] : rows,
    };
  });

  const doing = free.map((u) => `${workerLabel(rules, u)} ${idleDoing(rules, u)}`);

  // An empty building while a worker idles: more workers on that task fill it.
  const waiting = free.filter((u) => rosterIdle.has(u.id)).length;
  let warn: CityWorkers['warn'] = null;
  const sum = targetsTotal(city.workTargets);
  for (const row of tasks) {
    if (row.task === BUILD_TASK || waiting === 0) continue;
    const empty = row.places.find((p) => p.slots && p.slots.used === 0);
    if (!empty?.slots) continue;
    const target = Math.max(row.target, row.now) + Math.min(waiting, empty.slots.total);
    if (sum - row.target + target > cap) continue;
    const place = empty.name.split(' · ')[0];
    warn = {
      text: `${place} is empty: set ${row.name} to ${target} to fill it.`,
      task: row.task,
      target,
    };
    break;
  }

  const held: HeldRow[] = [];
  for (const u of mine) {
    const o = pinnedOrder(u);
    if (!o) continue;
    const row =
      o.kind === 'build' ? placeByKey.get(o.buildingId) : rowOfTile.get(getHexTileMapKey(o.target));
    let text = row?.name.split(' · ')[0] ?? 'a tile';
    if (o.kind === 'build') text = `${text} · building`;
    else {
      const res = row ? taskOfPlace.get(row.key) : tileResources(state, ctx, o.target)?.resourceId;
      text += ` · ${res ? `${rules.resources[res]?.name.toLowerCase() ?? res}` : 'gathering'}`;
    }
    held.push({ unitId: u.id, label: workerLabel(rules, u), text });
  }

  const visitors: VisitorRow[] = [];
  for (const p of places) {
    const u = unitOf(p.unitId);
    if (!u || p.kind !== 'visiting') continue;
    const row = p.key ? rowOfTile.get(p.key) : undefined;
    visitors.push({
      unitId: u.id,
      label: workerLabel(rules, u),
      from: cityNo(u.homeCityId),
      where: row?.name.split(' · ')[0] ?? wild.get(p.task)?.name ?? 'the field',
    });
  }
  visitors.sort((a, b) => byId(a.unitId, b.unitId));

  const away: CityWorkers['away'] = [];
  for (const p of input.away) {
    const u = unitOf(p.unitId);
    if (!u) continue;
    const there = Object.values(state.buildings).find(
      (b) => b.owner === city.owner && p.key !== null && keysOf(b).includes(p.key)
    );
    const name = there ? (rules.buildings[there.type]?.name ?? there.type) : null;
    const what = name ?? `wild ${rules.resources[p.task]?.name.toLowerCase() ?? p.task}`;
    away.push({
      unitId: u.id,
      label: workerLabel(rules, u),
      text: `${workerLabel(rules, u)} → ${cityNo(p.city)} ${what}`,
    });
  }
  away.sort((a, b) => byId(a.unitId, b.unitId));

  return {
    own: mine.length,
    cap,
    tasks,
    free: { unitIds: free.map((u) => u.id), idleIds, text: doing.join(' · ') },
    away,
    warn,
    held,
    visitors,
  };
}
